import React, { useCallback, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { MinimalCartEntryType } from 'features/cart/types';
import { useStoreContext } from 'features/store';
import { get } from 'lodash-es';
import { useDisclose } from 'native-base';
import { Platform } from 'react-native';

import { LoyaltyIncentiveSwapDialog } from 'components/loyalty-incentive-swap-dialog';
import type { LoyaltyIncentiveSwapDialogProps } from 'components/loyalty-incentive-swap-dialog';
import RestaurantRedemptionModal from 'components/offer-redemption-modal/redemption-modal';
import { toaster } from 'components/toaster/toaster';
import {
  LoyaltyPromotionType,
  OfferType,
  useCreateOrderlessTransactionMutation,
} from 'generated/graphql-gateway';
import { useFeatureEarningCalculationQuery } from 'generated/sanity-graphql';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import {
  getMenuItemDescriptorForIncentive,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { ModalRewardItemUnavailable } from 'pages/loyalty/loyalty-incentives-components/modal-reward-item-unavailable-modal';
import { useAppliedRewards } from 'pages/loyalty/loyalty-rewards/use-applied-reward';
import { EventTypes, logRBIEvent } from 'state/amplitude';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  getCartIdToEvictForNewRewardSlot,
  sumAppliedTimesApplied,
} from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { ICreateCartEntryParams } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart';
import { LoyaltyOffer, LoyaltyReward, isLoyaltyOffer, isReward } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';

import { AddToCartArgs, Incentive } from './types';

/**
 * Wraps the param Component in another component in order to add the function
 * addIncentiveToCart which takes an incentive and a redemption type and adds it to the loyaltyContext
 * It also handles and shows the related modals for each situation.
 * Eg: Shows "only one coupon allowed" if you already have an Offer in the cart.
 *
 * @param Component The component you want to wrap with this logic
 */

/**
 * Delay before navigating away after a modal is dismissed.
 * The native iOS modal close animation is still running when navigation fires
 * synchronously; without this buffer both dialogs briefly animate at the same time.
 */
export const MODAL_DISMISS_NAVIGATION_DELAY_MS = 100;

/**
 * Runs navigation either inline or after the iOS modal-close buffer. Calling it inline on Android
 * matters for performance: when the navigation update is batched with the surrounding state
 * dispatches into a single React commit, the source page unmounts in the same commit and its
 * subscribers never re-render with the new state. A `setTimeout`-based call, even with delay 0,
 * lands in a later macrotask, splits the work into two commits, and pins the JS thread for
 * seconds on Android.
 */
const runNavigationAfterModalDismiss = (action: () => void) => {
  if (Platform.OS === 'ios') {
    setTimeout(action, MODAL_DISMISS_NAVIGATION_DELAY_MS);
    return;
  }
  action();
};

const withAddIncentiveToCart = <T extends { selectedIncentive: LoyaltyReward | LoyaltyOffer }>(
  Component: React.FC<React.PropsWithChildren<T>>
) => {
  return (props: Omit<T, 'addIncentiveToCart'>) => {
    // Utils

    const router = useRouter();

    const { isOpen, onOpen, onClose } = useDisclose();
    const enableCooldownToast = useFlag(LaunchDarklyFlag.ENABLE_COOLDOWN_TOAST_INFO);
    const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
    const isUnifiedMenuEnabled = useEnableUnifiedMenu();
    const enableMinimalCartUi = useMinimalCartUi();
    const { indexedRewards } = useUnifiedMenuContext();

    // State
    const [incentiveName, setIncentiveName] = useState<string>('');
    const [selectedIncentive, setSelectedIncentive] = useState<Incentive | null>();
    const [showOfferRedemptionModal, setShowOfferRedemptionModal] = useState(false);

    // Context
    const { engineRewardsMap } = useLoyaltyRewardsList();
    const { noStoreSelected } = useStoreContext();
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
    const appliedRewards = useAppliedRewards();
    const rewardRedemptionLimit = useRewardRedemptionLimit();
    const dispatch = useAppDispatch();
    const { serviceMode } = useServiceModeContext();
    const localIncentiveArgs = useRef<AddToCartArgs>(undefined);
    // True when the swap was confirmed; tells handleSwapDialogAfterClose to call redeemIncentive.
    const pendingSwapRef = useRef(false);

    const {
      addDiscountOfferToMinimalCart,
      cartEntries: minimalCartEntries,
      removeItemFromMinimalCart,
    } = useMinimalCart();
    const { removeFromCart } = useCart();
    const {
      inRestaurantRedemptionCart,
      inRestaurantLoyaltyEnabledAtRestaurant,
      inRestaurantRedemptionEnabled,
      addInRestaurantRedemptionEntry,
      removeInRestaurantRedemptionEntry,
      enableOfferRedemption,
      shouldForceRestaurantSelection,
    } = useInRestaurantRedemptionContext();
    const { loyaltyUser } = useLoyaltyUser();
    // @ts-ignore
    const [InvalidIncentiveModal, openInvalidIncentiveModal] = useDialogModal({
      modalAppearanceEventMessage: 'Invalid In Restaurant Incentive Redemption',
      isErrorModal: true,
    });
    const [createOrderlessTransaction] = useCreateOrderlessTransactionMutation({
      variables: {
        input: {
          loyaltyId: loyaltyUser?.id,
          channel: 'ORDERLESS',
          incentives: [
            { id: selectedIncentive?.loyaltyEngineId || '', type: LoyaltyPromotionType.OFFER },
          ],
        },
      },
      onCompleted: () => {
        setShowOfferRedemptionModal(true);
      },
      onError: () => openInvalidIncentiveModal({ message: t('unableToPlaceOrder') }),
    });

    const { data: earningCalculationData } = useFeatureEarningCalculationQuery({
      variables: { id: 'earningCalculation' },
    });
    const diffInMin = getDifferenceToNow(
      DIFFERENCE_TIME_UNITS.MINUTES,
      loyaltyUser?.offerRedemptionAvailability?.availableAfter || ''
    );

    const showOfferCooldownToast = () => {
      //If the diffInMin is greater than 0 it means that the user already have an offer in course so we do nothing.
      if (!enableCooldownToast || diffInMin > 0 || !serviceMode) {
        return;
      }

      toaster.neutral(
        t('offersCooldownInfo', {
          period: earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod,
        })
      );
    };

    const [ItemUnavailableModal, openItemUnavailableModal] = useDialogModal<
      {},
      { rewardName: string }
    >({
      Component: ModalRewardItemUnavailable as any,
    });
    const [ReadyToRedeemDialog, openReadyToRedeemDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Ready to Redeem',
      onConfirm: () => {
        if (selectedIncentive && isLoyaltyOffer(selectedIncentive)) {
          const name = selectedIncentive.name;
          logRBIEvent({
            name: 'Legacy Offer Added to In Store Order',
            type: EventTypes.Other,
            attributes: {
              sanityId: selectedIncentive._id || '',
              // @ts-expect-error TS(2345) FIXME: Argument of type '{ readonly __typename?: "LocaleB... Remove this comment to see the full error message
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
            },
          });
        }
        if (loyaltyUser?.id && selectedIncentive?.loyaltyEngineId) {
          createOrderlessTransaction();
        } else {
          setShowOfferRedemptionModal(true);
        }
        if (selectedIncentive && isLoyaltyOffer(selectedIncentive)) {
          showOfferCooldownToast();
        }
      },
      showCancel: false,
    });
    const {
      SwitchCartMethodDialog,
      shouldShowSwitchToMobileDialog,
      shouldShowSwitchToStoreDialog,
      openSwitchCartMethod,
    } = useSwitchCartMethodDialog();

    // Handle add to cart flow
    const addIncentiveToCart = (args: AddToCartArgs) => {
      setSelectedIncentive(args.incentive);
      localIncentiveArgs.current = args;

      if (isLoyaltyOffer(args.incentive)) {
        const hasAppliedMobileOfferInMinimalCart =
          enableMinimalCartUi &&
          minimalCartEntries.some(
            entry =>
              entry.type === MinimalCartEntryType.discount ||
              entry.type === MinimalCartEntryType.incentive
          );
        const hasAppliedMobileOffer =
          appliedOffers.length > 0 || hasAppliedMobileOfferInMinimalCart;
        const isSameMobileOfferApplied =
          appliedOffers.some(offer => offer.id === args.incentive.loyaltyEngineId) ||
          (enableMinimalCartUi &&
            minimalCartEntries.some(
              entry =>
                (entry.type === MinimalCartEntryType.discount ||
                  entry.type === MinimalCartEntryType.incentive) &&
                entry.referenceId === args.incentive.loyaltyEngineId
            ));
        const hasAppliedInRestaurantOffer = inRestaurantRedemptionCart.some(entry =>
          Boolean(get(entry, 'details.offer'))
        );
        const isSameInRestaurantOfferApplied = inRestaurantRedemptionCart.some(entry => {
          const appliedOffer = get(entry, 'details.offer');

          return (
            appliedOffer &&
            (appliedOffer._id === args.incentive._id ||
              appliedOffer.loyaltyEngineId === args.incentive.loyaltyEngineId)
          );
        });
        const showReplaceModal =
          (hasAppliedMobileOffer && !isSameMobileOfferApplied) ||
          (hasAppliedInRestaurantOffer && !isSameInRestaurantOfferApplied);

        if (showReplaceModal) {
          return onOpen();
        }
      }

      const evictCandidate = getCartIdToEvictForNewRewardSlot(
        appliedLoyaltyRewards,
        '',
        rewardRedemptionLimit
      );
      const atCapacityForAnotherReward =
        enableRewardUnstacking &&
        isReward(args.incentive) &&
        Boolean(evictCandidate) &&
        !appliedRewards.some(r => r.rewardId === args.incentive._id);

      if (isReward(args.incentive) && enableRewardUnstacking && atCapacityForAnotherReward) {
        return onOpen();
      }

      redeemIncentive(args);
    };

    const redeemIncentive = (args: AddToCartArgs) => {
      if (args.method === RewardRedemptionMethod.MOBILE) {
        return handleMobileRedemption(args);
      }
      tryInRestaurantRedemption(args);
    };

    const tryInRestaurantRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToStoreDialog) {
        openSwitchCartMethod({
          onConfirm: () => handleInRestaurantRedemption(args),
        });
      } else {
        handleInRestaurantRedemption(args);
      }
    };

    const handleMobileRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToMobileDialog) {
        openSwitchCartMethod({ onConfirm: () => mobileRedemption(args) });
      } else {
        mobileRedemption(args);
      }
    };

    const handleInRestaurantRedemption = (args: AddToCartArgs) => {
      const isInStoreLoyaltyRedemptionEnabled =
        inRestaurantRedemptionEnabled && inRestaurantLoyaltyEnabledAtRestaurant;
      const shouldRedeemInRestaurant =
        isInStoreLoyaltyRedemptionEnabled && (isReward(args.incentive) || enableOfferRedemption);

      if (shouldRedeemInRestaurant) {
        inRestaurantRedemption(args);
      } else {
        openReadyToRedeemDialog();
      }
    };

    const clearExistingOffersForSwap = useCallback(
      (skipInRestaurantOfferEntries = false) => {
        appliedOffers.forEach(offer => {
          if (offer.cartId) {
            removeFromCart({ cartId: offer.cartId });
          }
        });

        if (enableMinimalCartUi) {
          minimalCartEntries
            .filter(
              entry =>
                entry.type === MinimalCartEntryType.discount ||
                entry.type === MinimalCartEntryType.incentive
            )
            .forEach(entry => {
              removeItemFromMinimalCart(entry);
            });
        }

        // When the destination is mobile redemption, leave in-restaurant offer entries intact.
        // SwitchCartMethodDialog will show next and clear them via resetInRestaurantRedemption()
        // only if the user confirms. Clearing here would mean a subsequent cancel of
        // SwitchCartMethodDialog leaves the user with both carts empty — broken state.
        if (!skipInRestaurantOfferEntries) {
          inRestaurantRedemptionCart
            .filter(entry => Boolean(get(entry, 'details.offer')))
            .forEach(entry => {
              removeInRestaurantRedemptionEntry(entry);
            });
        }

        dispatch(actions.loyalty.resetAppliedOffers());
        dispatch(actions.loyalty.setSelectedOffer(null));
      },
      [
        appliedOffers,
        dispatch,
        enableMinimalCartUi,
        inRestaurantRedemptionCart,
        minimalCartEntries,
        removeFromCart,
        removeInRestaurantRedemptionEntry,
        removeItemFromMinimalCart,
      ]
    );

    // TODO: Analyze converting these methods to useCallbacks
    const mobileRedemption = ({ incentive }: AddToCartArgs) => {
      if (isLoyaltyOffer(incentive)) {
        dispatch(actions.loyalty.setSelectedOffer(incentive));

        if (isDiscountLoyaltyOffer(incentive)) {
          if (!incentive.isStackable) {
            appliedOffers.forEach(offer => {
              // removes offer from cart if the applied offer is not stackable
              if (!offer.isStackable && !!offer.cartId) {
                removeFromCart({ cartId: offer.cartId });
              }
            });
          }

          // Discount offers should not show menu item details
          dispatch(
            actions.loyalty.applyOffer({
              id: incentive.loyaltyEngineId,
              type: OfferType.GLOBAL,
              isStackable: incentive.isStackable,
              cmsId: incentive._id,
              cartId: DISCOUNT_OFFER_CART_ID,
            })
          );

          if (isUnifiedMenuEnabled) {
            addDiscountOfferToMinimalCart(incentive);
          }

          toaster.positive(t('offerAddedToCart'));
          router.replace(routes.menu);
          return;
        }
      }

      const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];

      let incentiveMenuPath: string | undefined;
      if (isUnifiedMenuEnabled) {
        if (isLoyaltyOffer(incentive)) {
          incentiveMenuPath = `${routes.menu}/offer/${incentive._id}`;
        }
        if (isReward(incentive)) {
          const rewardEntry = indexedRewards.find(incentive?._id);
          const rewardEntryBenefitId = rewardEntry?.options?.[0]?.entryId;
          const urlParams = new URLSearchParams();
          if (rewardEntry?.id) {
            urlParams.set('rewardId', rewardEntry.id);
          }
          if (engineReward?.rewardBenefitId) {
            urlParams.set('rewardBenefitId', engineReward.rewardBenefitId);
          }
          if (rewardEntryBenefitId) {
            incentiveMenuPath = `${
              routes.menu
            }/reward/${rewardEntryBenefitId}?${urlParams.toString()}`;
          }
        }
      } else {
        const menuItemDescriptor = getMenuItemDescriptorForIncentive(
          incentive,
          engineReward?.rewardBenefitId
        );
        incentiveMenuPath = makeUrlForIncentive(incentive, menuItemDescriptor);
        if (!menuItemDescriptor || !incentiveMenuPath) {
          logger.warn(
            `Attempting to make a url for reward that is missing data: reward ${incentive?._id}`
          );
          const incentiveData = parseIncentiveData(incentive);
          setIncentiveName(incentiveData.name);
          openItemUnavailableModal();
          return;
        }
      }

      // there seems to be an issue with the router.navigate if a react dialog is open when pushing.
      // this allows the "one offer per order" dialog to close before pushing on iOS.
      // On Android we navigate synchronously so React can batch the navigation update with the
      // preceding state dispatches into a single commit — see runNavigationAfterModalDismiss for
      // the rationale.
      runNavigationAfterModalDismiss(() => {
        router.navigate(incentiveMenuPath!);
      });

      if (isLoyaltyOffer(incentive)) {
        showOfferCooldownToast();
      }
    };

    const createInRestaurantCartEntry = useCallback(
      (incentive: AddToCartArgs['incentive']) => {
        let inRestaurantCartEntry: ICreateCartEntryParams | undefined;
        if (incentive && !isReward(incentive)) {
          inRestaurantCartEntry = { offer: incentive };
        } else {
          const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
          if (incentive && engineReward) {
            inRestaurantCartEntry = {
              reward: (incentive as unknown) as LoyaltyReward,
              engineReward,
            };
          }
        }
        return inRestaurantCartEntry;
      },
      [engineRewardsMap]
    );

    const inRestaurantRedemption = ({ incentive }: AddToCartArgs) => {
      const shouldSelectStore = shouldForceRestaurantSelection && noStoreSelected;
      if (shouldSelectStore) {
        router.navigate(routes.storeLocator);
        return;
      }

      const inRestaurantCartEntry = createInRestaurantCartEntry(incentive);
      if (inRestaurantCartEntry) {
        addInRestaurantRedemptionEntry(inRestaurantCartEntry);

        // Mirrors the same delay used in mobileRedemption: the swap dialog's native close
        // animation is still running when router.replace fires synchronously. Without this
        // delay the dismissed dialog and the next one animate simultaneously on iOS, making
        // both variants briefly visible at the same time. On Android we navigate synchronously
        // so React can batch the navigation update with the preceding state dispatches.
        runNavigationAfterModalDismiss(() => {
          router.replace(routes.myCode);
        });

        if (isLoyaltyOffer(incentive)) {
          showOfferCooldownToast();
        }
      }
    };

    const onDismissRestaurantRedemptionModal = () => {
      router.dismiss();
      dispatch(actions.loyalty.setShouldRefetchOffers(true));
      setShowOfferRedemptionModal(false);
    };

    const isSelectedIncentiveReward = isReward(props.selectedIncentive);

    // Called by Dialog once the exit animation has fully completed. Reads pendingSwapRef and
    // localIncentiveArgs at call time — no-op if the dialog was cancelled rather than confirmed.
    // Clearing happens here rather than in onLoyaltyIncentiveSwapConfirm so the cart is only
    // mutated after the user has committed to the full swap and all animations have finished.
    const handleSwapDialogAfterClose = () => {
      if (!pendingSwapRef.current) {
        return;
      }
      pendingSwapRef.current = false;
      if (localIncentiveArgs.current) {
        if (isLoyaltyOffer(localIncentiveArgs.current.incentive)) {
          const isMobileRedemption =
            localIncentiveArgs.current.method === RewardRedemptionMethod.MOBILE;
          clearExistingOffersForSwap(isMobileRedemption);
        }
        redeemIncentive(localIncentiveArgs.current);
      }
    };

    const onLoyaltyIncentiveSwapConfirm = () => {
      onClose();
      if (localIncentiveArgs?.current) {
        pendingSwapRef.current = true;
      }
    };

    const loyaltySwapDialogProps: LoyaltyIncentiveSwapDialogProps = isSelectedIncentiveReward
      ? {
          incentiveType: LoyaltyPromotionType.REWARD,
          maxRewards: rewardRedemptionLimit,
          appliedRewards: sumAppliedTimesApplied(appliedLoyaltyRewards),
          isOpen,
          onClose,
          onSwap: onLoyaltyIncentiveSwapConfirm,
          onAfterClose: handleSwapDialogAfterClose,
        }
      : {
          incentiveType: LoyaltyPromotionType.OFFER,
          isOpen,
          onClose,
          onSwap: onLoyaltyIncentiveSwapConfirm,
          onAfterClose: handleSwapDialogAfterClose,
        };

    return (
      <>
        <LoyaltyIncentiveSwapDialog {...loyaltySwapDialogProps} />
        <SwitchCartMethodDialog />
        <ItemUnavailableModal rewardName={incentiveName} />
        {selectedIncentive && isLoyaltyOffer(selectedIncentive) && (
          <>
            <InvalidIncentiveModal heading={t('uhoh')} showCloseButton />
            <ReadyToRedeemDialog
              testID="ready-to-redeem-dialog"
              heading={t('couponWillExpire')}
              body={t('confirmDialogBody')}
              buttonLabel={t('redeem')}
              showCloseButton
            />
            {showOfferRedemptionModal && (
              <RestaurantRedemptionModal
                selectedOffer={selectedIncentive}
                redemptionStartTime={Date.now()}
                onDismiss={onDismissRestaurantRedemptionModal}
              />
            )}
          </>
        )}

        <Component {...(props as T)} addIncentiveToCart={addIncentiveToCart} />
      </>
    );
  };
};

export default withAddIncentiveToCart;
